import { apiClient, newIdempotencyKey } from './client'
import type { CursorPage } from './accounts'
import type { ContactView, CreateContactRequest, PatchContactRequest } from '../types/contact'

export interface ListContactsParams {
  /** RSQL, already combined by the caller (see lib/rsql.ts). */
  filter?: string | null
  /** `field,-field2` — backend allow-lists name/email/createdAt/id (design §2.3 RSQL). */
  sort?: string | null
  cursor?: string | null
  /** Clamped to 200 server-side (Pagination.normalizeLimit); default page size here is 50. */
  limit?: number
}

function toQuery(params: ListContactsParams): string {
  const q = new URLSearchParams()
  if (params.filter) q.set('filter', params.filter)
  if (params.sort) q.set('sort', params.sort)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

export async function listContacts(
  params: ListContactsParams,
  signal?: AbortSignal,
): Promise<CursorPage<ContactView>> {
  const { data } = await apiClient.get<CursorPage<ContactView>>(`/contacts?${toQuery(params)}`, { signal })
  return data
}

/** Backs `screens/contacts/ContactDetailScreen.tsx` — `GET /api/v1/contacts/{id}` (already published by `ContactController`). */
export async function getContact(
  id: string,
  signal?: AbortSignal,
): Promise<{ contact: ContactView; version: number }> {
  const { data, version } = await apiClient.get<ContactView>(`/contacts/${id}`, { signal })
  return { contact: data, version: version ?? data.version }
}

export async function createContact(
  request: CreateContactRequest,
): Promise<{ contact: ContactView; version: number }> {
  const { data, version } = await apiClient.post<ContactView>('/contacts', request, {
    idempotencyKey: newIdempotencyKey(),
  })
  return { contact: data, version: version ?? data.version }
}

export async function patchContact(
  id: string,
  request: PatchContactRequest,
  ifMatchVersion: number,
): Promise<{ contact: ContactView; version: number }> {
  const { data, version } = await apiClient.patch<ContactView>(`/contacts/${id}`, request, {
    ifMatchVersion,
  })
  return { contact: data, version: version ?? data.version }
}
